export const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';
export const DEFAULT_USA_DATE_FORMAT = 'MM/DD/YYYY';
export const DEFAULT_DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm:ss A';
export const DEFAULT_DATE_TIME_FORMAT_LOWER_CASE = 'DD/MM/YYYY HH:mm:ss a';
export const DEFAULT_DATE_TIME_FORMAT_SHORT = 'D/M/YYYY HH:mm:ss A';
export const STEPPER_DATE_FORMAT = 'D/M/YY · h:mm a';
export const DATE_TIME_FORMAT_AM_PM = 'D/M/YY h:mma';
export const DATE_TIME_FORMAT_AM_PM_UPPER = 'D/M/YY h:mm A';
export const HEADER_DATE_FORMAT = 'D MMM, YYYY';
export const WIDGET_DATE_FORMAT = 'D MMM YYYY';
export const LOGIN_DATE_FORMAT = 'DD/MM/YYYY HH:mm:ss';
export const LOGIN_DATE_FORMAT2 = 'DD/MM/YYYY HH:mma';

export const DATE_MONTH_SHORT = 'DD MMM';
export const DATE_MONTH_YEAR_SHORT = 'DD MMM YYYY';
export const DAY_DATE_SHORT = 'ddd, D MMM';

/** @deprecated Use DateTimeFormat instead */
export const DateFormat = {
  Default: DEFAULT_DATE_FORMAT,
  DefaultUSA: DEFAULT_USA_DATE_FORMAT,
  DateTime: DEFAULT_DATE_TIME_FORMAT,
  DateTimeLower: DEFAULT_DATE_TIME_FORMAT_LOWER_CASE,
  DateTimeShort: DEFAULT_DATE_TIME_FORMAT_SHORT,
  Stepper: STEPPER_DATE_FORMAT,
  AmPm: DATE_TIME_FORMAT_AM_PM,
  AmPmUpper: DATE_TIME_FORMAT_AM_PM_UPPER,
  Header: HEADER_DATE_FORMAT,
  Widget: WIDGET_DATE_FORMAT,
  Login: LOGIN_DATE_FORMAT,
  Login2: LOGIN_DATE_FORMAT2,
  DateMonthShort: DATE_MONTH_SHORT,
  DateMonthYearShort: DATE_MONTH_YEAR_SHORT,
  DayDateShort: DAY_DATE_SHORT,
} as const;
export type DateFormat = (typeof DateFormat)[keyof typeof DateFormat];

/** ISO date/time formats. */
export const IsoDateTimeFormat = {
  /** ISO date. */
  IsoDate: 'YYYY-MM-DD',
  /** ISO time (24h). */
  IsoTime: 'HH:mm:ss',
  /** ISO datetime (no TZ). */
  IsoDateTime: 'YYYY-MM-DDTHH:mm:ss',
  /** ISO datetime with offset, e.g. +11:00. */
  IsoDateTimeTz: 'YYYY-MM-DDTHH:mm:ssZZ',
  /** ISO UTC datetime with literal 'Z'. Use with isUtc=true. */
  IsoDateTimeZ: 'YYYY-MM-DDTHH:mm:ss[Z]',
} as const;
export type IsoDateTimeFormat =
  (typeof IsoDateTimeFormat)[keyof typeof IsoDateTimeFormat];

/** Human-friendly date/time formats. */
export const HumanDateTimeFormat = {
  /** e.g. '1 Nov' */
  HumanDateShort: 'D MMM',
  /** e.g. '01 Nov 2025' */
  HumanDateMedium: 'DD MMM YYYY',
  /** e.g. 'Sat, 1 Nov' */
  HumanDayDateShort: 'ddd, D MMM',
  /** e.g. '1 Nov 2025 15:42' */
  HumanDateTimeShort: 'D MMM YYYY HH:mm',
  /** e.g. '01 Nov 2025 15:42' */
  HumanDateTimeMedium: 'DD MMM YYYY HH:mm',
  /** e.g. '01 Nov 2025 15:42:07' */
  HumanDateTimeWithSeconds: 'DD MMM YYYY HH:mm:ss',
  /** e.g. '1 Nov 2025 3:42 pm' */
  HumanDateTimeShort12h: 'D MMM YYYY h:mm a',
  /** e.g. '01 Nov 2025 3:42 pm' */
  HumanDateTimeMedium12h: 'DD MMM YYYY h:mm a',
  /** e.g. '01 Nov 2025 3:42:07 pm' */
  HumanDateTimeWithSeconds12h: 'DD MMM YYYY h:mm:ss a',
} as const;
export type HumanDateTimeFormat =
  (typeof HumanDateTimeFormat)[keyof typeof HumanDateTimeFormat];

/** Other human-friendly date/time formats. */
export const OtherDateTimeFormat = {
  /** Stable, sortable log timestamp without TZ. */
  LogDateTime: 'YYYY-MM-DD HH:mm:ss',
  /** Stable log timestamp with TZ offset. */
  LogDateTimeTz: 'YYYY-MM-DD HH:mm:ss ZZ',
  /** Compact date for filenames/IDs. */
  CompactDate: 'YYYYMMDD',
  /** Compact datetime for filenames/IDs. */
  CompactDateTime: 'YYYYMMDD_HHmmss',
  /** Legacy header look, e.g. '1 Nov, 2025'. */
  HeaderLike: 'D MMM, YYYY',
  /** Xemplo login format. */
  Login: 'DD/MM/YYYY HH:mm:ss',
  /** Alt Xemplo login format. */
  Login2: 'DD/MM/YYYY HH:mma',
  /** 12h datetime with lowercase meridiem, e.g. '01/11/2025 03:42:07 pm'. */
  DateTime12Lower: 'DD/MM/YYYY hh:mm:ss a',
} as const;
export type OtherDateTimeFormat =
  (typeof OtherDateTimeFormat)[keyof typeof OtherDateTimeFormat];

/** Time-only formats. */
export const TimeFormat = {
  /** 24-hour time, e.g. '15:42'. */
  HourMinute24: 'H:mm',
  /** 12-hour time, e.g. '3:42 PM'. */
  HourMinute12: 'h:mm A',
} as const;
export type TimeFormat = (typeof TimeFormat)[keyof typeof TimeFormat];

/**
 * v2: universal, semantic, ISO-friendly date/time formats.
 *
 * @example
 * ```
 * // Instance usage:
 * const helper = new DateTimeHelper({ format: DateTimeFormat.Default });
 * helper.format('2025-11-01');                                   // '01/11/2025'
 * helper.format('2025-11-01T15:42:00Z', { isUtc: true });        // '01/11/2025'
 * helper.format(new Date(), { format: DateTimeFormat.IsoDate }); // '2025-11-01'
 *
 * // Static usage:
 * DateTimeHelper.format('2025-11-01');          // '01/11/2025'
 * DateTimeHelper.format('2025-11-01T15:42:00Z', { isUtc: true }); // '01/11/2025'
 * DateTimeHelper.format(new Date(), { format: DateTimeFormat.IsoDate }); // '2025-11-01'
 *
 * Conventions:
 * - `Iso*` are machine-friendly and sortable.
 * - `Human*` are UI-friendly.
 * - `Log*` are stable for logs.
 * - `Compact*` are filename/ID friendly.
 * - `HourMinute*` are time-only helpers.
 * ```
 */
export const DateTimeFormat = {
  /** Default AU-style date (DD/MM/YYYY). */
  Default: 'DD/MM/YYYY',
  DefaultUSA: DEFAULT_USA_DATE_FORMAT,
  ...IsoDateTimeFormat,
  ...HumanDateTimeFormat,
  ...OtherDateTimeFormat,
  ...TimeFormat,
} as const;

// Avoid value/type name shadowing; clearer at call sites and exports.
export type DateTimeFormat = (typeof DateTimeFormat)[keyof typeof DateTimeFormat];

// Dayjs provides the build in validation for the dates
// However to bypass the single or double digit day/month issue, it needs these formats during validation
export const DayjsFormatMapper = {
  [DateTimeFormat.Default]: 'D/M/YYYY',
  [DateTimeFormat.DefaultUSA]: 'M/D/YYYY',
};

export type DayjsFormatMapper = keyof typeof DayjsFormatMapper;
